import { z } from 'zod';
import { blockTagId, deterministicChildId, newId } from '../ids.js';
import { blockRowSchema, instantSchema, stageRowSchema, taxonomyRowSchema, uuidSchema, } from '../schema/rows.js';
import { sortKeyAfter } from '../sort-keys.js';
import { DomainError } from './types.js';
/**
 * Quick capture (docs/04 §1, CAP-01..03): one block with its dates and tags, plus the tags that
 * do not exist yet, in a single local transaction. The caller resolves the parsed tags against
 * the existing ones (by `normalizeTagName`); new ones get a deterministic id per workspace,
 * taxonomy and normalized path, so two offline devices creating the same tag converge (S4).
 */
/** Tag name as compared and hashed: lower case, no accents, `-`/`_` as spaces, no `·`. */
export function normalizeTagName(name) {
    return name
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/·/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Deterministic id of a tag created from its path (`['trabajo', 'clientes']` for `#trabajo/clientes`). */
export function capturedTagId(workspaceId, taxonomyId, path) {
    const normalized = path.map(normalizeTagName).join('/');
    return deterministicChildId(workspaceId, `tag:${taxonomyId}:${normalized}`);
}
const tagSegmentInput = z.object({
    /** Name as written, without the prefix. */
    name: z.string().trim().min(1).max(200),
    /** Id of the existing tag with this name under the previous segment, if any. */
    existing_id: uuidSchema.optional(),
});
export const capturedTagInput = z.object({
    taxonomy: taxonomyRowSchema.pick({ id: true, workspace_id: true, cardinality: true }),
    path: z.array(tagSegmentInput).min(1),
});
/**
 * Tags to create (new ones, with their parents) and the leaves to join, for tags written in a
 * capture or accepted in the triage. Tags must belong to the workspace (INV-11); in a `one`
 * taxonomy a later tag replaces the earlier one (INV-05).
 */
export function tagWrites(workspaceId, tags, now) {
    const tagOps = new Map();
    const leaves = new Map();
    for (const tag of tags) {
        const { taxonomy } = tag;
        if (taxonomy.workspace_id !== workspaceId) {
            throw new DomainError('invariant_violation', 'La etiqueta es de otro espacio.', 'INV-11');
        }
        let parentId = null;
        const names = [];
        for (const segment of tag.path) {
            names.push(segment.name);
            let tagId;
            if (segment.existing_id) {
                tagId = segment.existing_id;
            }
            else {
                tagId = capturedTagId(workspaceId, taxonomy.id, names);
                if (!tagOps.has(tagId)) {
                    tagOps.set(tagId, {
                        table: 'tags',
                        op: 'PUT',
                        id: tagId,
                        data: {
                            workspace_id: workspaceId,
                            taxonomy_id: taxonomy.id,
                            parent_id: parentId,
                            name: segment.name,
                            color: null,
                            icon: null,
                            sort_key: sortKeyAfter(null),
                            created_at: now,
                        },
                    });
                }
            }
            parentId = tagId;
        }
        const leafId = parentId;
        // INV-05: in a `one` taxonomy a later tag replaces the earlier one.
        const slot = taxonomy.cardinality === 'one' ? `one:${taxonomy.id}` : `tag:${leafId}`;
        leaves.delete(slot);
        leaves.set(slot, { tagId: leafId, taxonomyId: taxonomy.id });
    }
    return { tags: [...tagOps.values()], leaves: [...leaves.values()] };
}
const captureItemInput = z.object({
    id: uuidSchema.optional(),
    workspace_id: uuidSchema,
    created_by: uuidSchema,
    title: z.string().trim().min(1, 'El título no puede estar vacío').max(2000),
    due_on: blockRowSchema.shape.due_on.default(null),
    start_on: blockRowSchema.shape.start_on.default(null),
    sort_key: z.string().min(1),
    /** Destination stage: the capture stage (Inbox) or another task stage. */
    stage: stageRowSchema.pick({ id: true, workspace_id: true, category: true, kind_scope: true }),
    tags: z.array(capturedTagInput).default([]),
    now: instantSchema.optional(),
});
/**
 * Captures an element. In an Inbox stage it is an `item`; in any other task stage, a `task`
 * (INV-04). Stage and tags must belong to the workspace (INV-11); with a `one` taxonomy only the
 * last tag is kept (INV-05).
 */
export function captureItem(input) {
    const parsed = captureItemInput.parse(input);
    const { workspace_id: workspaceId, stage } = parsed;
    if (stage.workspace_id !== workspaceId) {
        throw new DomainError('invariant_violation', 'La etapa es de otro espacio.', 'INV-11');
    }
    if (stage.kind_scope !== 'task') {
        throw new DomainError('invariant_violation', 'Solo se captura en etapas de tareas.', 'INV-04');
    }
    const kind = stage.category === 'inbox' ? 'item' : 'task';
    const now = parsed.now ?? new Date().toISOString();
    const blockId = (parsed.id ?? newId());
    const { tags: tagOps, leaves } = tagWrites(workspaceId, parsed.tags, now);
    const block = {
        table: 'blocks',
        op: 'PUT',
        id: blockId,
        data: {
            workspace_id: workspaceId,
            parent_id: null,
            sort_key: parsed.sort_key,
            kind,
            title: parsed.title,
            body: null,
            stage_id: stage.id,
            stage_changed_at: now,
            status: 'open',
            due_on: parsed.due_on,
            start_on: parsed.start_on,
            created_by: parsed.created_by,
            props: {},
            created_at: now,
        },
    };
    const joins = leaves.map((leaf) => ({
        table: 'block_tags',
        op: 'PUT',
        id: blockTagId(blockId, leaf.tagId),
        data: {
            workspace_id: workspaceId,
            block_id: blockId,
            tag_id: leaf.tagId,
            taxonomy_id: leaf.taxonomyId,
            created_at: now,
        },
    }));
    return [...tagOps, block, ...joins];
}
//# sourceMappingURL=capture.js.map